// @flow
import { colors } from 'bench-style-constants';
import { darken } from 'color-helpers';
import dialogsManager from 'app-common/dialogs-manager';
// $FlowFixMe - TS file
import env from 'bench-environment';
import { Icon, toastSuccess, toastError } from 'bench-internal-theme';
import { matchPath, NavLink } from 'react-router-dom';
// $FlowFixMe - TS file
import React, { useState } from 'react';
import { Modal } from 'modal-components';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from './features';
import styled, { createGlobalStyle, css } from 'styled-components';
import ClientsEventsCounter from './ClientsEventsCounter';
import type { Client } from './BackboneAppContainer';
import { openModal } from '../modal-core-components';
import EditLedger from '../../src/ui/bk/editLedgerNew/EditLedger';
// $FlowFixMe - TS file
import { TaskNotification } from '../task-manager';
// $FlowFixMe - TS file
import { apiFetch } from '../data-api/util/fetch';
import { WrappedSpinner } from 'spinner';

// Notification counters are rendered by old logic written in CoffeeScript
// @see bk-tools-app/backbone/mainapp/app/common/js/component/inbox-events.coffee
const LEGACY_NOTIFICATION_COUNT_WRAPPER_CLASS = 'menu-item-counter-wrapper';
const LEGACY_NOTIFICATION_COUNT_CLASS = 'menu-item-counter';

// eslint-disable-next-line no-unused-expressions
const GlobalStyle = createGlobalStyle`
    .${LEGACY_NOTIFICATION_COUNT_CLASS} {
        background-color: ${colors.copper};
        border-radius: 4px;
        color: ${colors.white};
        padding: 0 8px;
        margin-right: 8px;

        &:hover {
            color: ${colors.white};
        }
    }
`;

// Unicode character sequences for country flag icons
const flagMap = {
  CA: '\ud83c\udde8\ud83c\udde6',
  US: '\ud83c\uddfa\ud83c\uddf8',
};

const SidebarLayout = styled.div`
  bottom: 0;
  display: grid;
  grid-template-rows: max-content max-content 1fr max-content;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const BenchHeader = styled.div`
  color: ${colors.white};
  font-size: 14px;
  background: ${colors.charcoal};
  padding: 12px 3px 12px 16px;
  position: relative;
`;

const DashboardFloLink = styled.a`
  color: ${colors.white};
  position: relative;
  font-size: 14px;
  line-height: 16px;
  padding-left: 20px;

  svg {
    position: absolute;
    left: 0;
    top: 1px;
  }
`;

const ClientViewLink = styled.a`
  background: ${props => (props.grayBackground ? colors.smoke : colors.water)};
  border-radius: 2px;
  color: ${colors.white};
  font-size: 12px;
  line-height: 16px;
  padding: 4px 10px;
  margin: 0 4px;
`;

const ClientHeader = styled.div`
  background: ${colors.charcoal};
  color: ${colors.white};
  font-size: 16px;
  line-height: 18px;
  // Requires additional padding on the right to accommodate the clients events notifications badge
  padding: 16px 40px 16px 16px;
  position: relative;
`;

const NavLayout = styled.div`
  background: ${colors.charcoal};
  overflow: auto;
`;

const NavSection = styled.div`
  color: ${colors.cloud};
  margin-bottom: 16px;
`;

const NavHeader = styled.div`
  font-size: 12px;
  line-height: 16px;
  padding: 4px 16px;
`;

const NavButton = styled.div`
  color: ${colors.stem};
  font-size: 14px;
  line-height: 16px;
  padding: 4px 16px;
`;

const rightPointingChevronSVGUrl = encodeURIComponent(`
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFFFFF" d="M9.67 20.30l-1.34 -1.32l7.09 -7.09l-7.09 -7.09l1.34 -1.32l8.41 8.41z" />
  </svg>
`);

const SidebarLinkStyle = css`
  color: ${colors.white};
  display: flex;
  font-size: 14px;
  justify-content: space-between;
  line-height: 16px;
  padding: 4px 16px;
  position: relative;

  &.active {
    background-color: ${darken(colors.charcoal, 0.1)};
    background-image: url('data:image/svg+xml;utf8,${rightPointingChevronSVGUrl}');
    background-position: 100% 50%;
    background-repeat: no-repeat;
    background-size: 32px 16px;
  }
`;

const NavItem = styled(NavLink)`
  ${SidebarLinkStyle}
`;

const SidebarLink = styled.a`
  ${SidebarLinkStyle}
`;

const NavBottomCluster = styled.div`
  align-items: stretch;
  background: ${colors.charcoal};
  display: flex;
  height: 40px;
`;

const NavBottomClusterAction = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-grow: 1;
  justify-content: center;

  &:hover {
    background: ${darken(colors.charcoal, 0.1)};
  }
`;

const NavBottomClusterLink = NavBottomClusterAction.withComponent('a');

type BkToolsSidebarProps = {
  clientId: string,
  client: ?Client,
  pathPrefix: string,
  patternPrefix: string,
};

const openAddLedger = clientId => {
  openModal(closeModal => (
    <Modal size="xLarge" animateIn>
      <EditLedger clientId={clientId} isQuickAdd onClose={closeModal} />
    </Modal>
  ));
};

const BkToolsSidebar = ({ clientId, client, pathPrefix, patternPrefix }: BkToolsSidebarProps) => {
  const clientCompany = client ? client.company : '';
  const clientCountryCode = client?.countryCode;
  // $FlowFixMe - Client object has additional fields that are not typed.
  const clientBookkeeperId = client?.assignedPersons?.bookkeepers?.primary?.id;

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;
  const isInternalPlaidOcrEnabled =
    features[SplitFeatureFlags.INTERNAL_PLAID_OCR]?.value === FeatureValue.FEATURE_ON;

  const [isRunningMito, setIsRunningMito] = useState(false);

  const handleRunMito = async () => {
    if (!clientId || isRunningMito) return;

    setIsRunningMito(true);

    try {
      const response = await apiFetch(`/mito.json/${clientId}`, {
        method: 'POST',
      });

      if (response && response.success === 'OK') {
        toastSuccess(`MITO completed successfully`, `${response.numberOfMatches} matches found.`);
      } else {
        toastError('MITO error', 'MITO completed but returned an unexpected response.');
      }
    } catch (error) {
      toastError('MITO error', 'Failed to run MITO. Please try again.');
      console.error('MITO error:', error);
    } finally {
      setIsRunningMito(false);
    }
  };

  return (
    <SidebarLayout>
      <BenchHeader>
        <DashboardFloLink href={`${env.location.dashboard}`} target="_blank">
          <Icon color={colors.white} icon="bench" size="small" />
        </DashboardFloLink>
        {clientId && (
          <ClientViewLink href={`${env.location.cwa}/client/${clientId}/`} target="_blank">
            Client View
          </ClientViewLink>
        )}
      </BenchHeader>
      <ClientHeader>
        {clientCompany} {clientCountryCode ? flagMap[clientCountryCode] : null}
        {clientId && (
          <ClientsEventsCounter localUrlPattern={new RegExp(`^/app/${clientId}(/|$)`)} />
        )}
      </ClientHeader>
      <NavLayout>
        <NavSection>
          <NavHeader>Dashboard</NavHeader>
          <NavItem aria-label="Messages" to={`${pathPrefix}/new-dashboard`}>
            Messages
          </NavItem>
          <NavItem aria-label="Task Manager" to={`${pathPrefix}/task-manager`}>
            Task Manager
            <TaskNotification clientId={clientId} />
          </NavItem>
          {clientBookkeeperId ? (
            <SidebarLink
              href={`${env.location.dashboard}/?bookkeeperId=${clientBookkeeperId}&q=${clientCompany}`}
              target="_blank"
            >
              FLO
            </SidebarLink>
          ) : null}
        </NavSection>
        <NavSection>
          <NavHeader>Connections</NavHeader>
          <NavItem aria-label="Accounts" to={`${pathPrefix}/new-accounts`}>
            Accounts
          </NavItem>
        </NavSection>
        <NavSection>
          <NavHeader>Documents</NavHeader>
          <NavItem aria-label="Upload" to={`${pathPrefix}/upload`}>
            Upload
          </NavItem>
          <NavItem
            aria-label="Folders"
            to={`${pathPrefix}/new-folders`}
            /* This is a special "magic" ID the bootstrap app uses to setup the counters. */
            id="new-folders-menu-item"
          >
            Folders
            <span className={LEGACY_NOTIFICATION_COUNT_WRAPPER_CLASS} />
          </NavItem>
        </NavSection>
        <NavSection>
          <NavHeader>Reports</NavHeader>
          <NavItem aria-label="Transaction Manager" to={`${pathPrefix}/transaction-manager`}>
            Transaction Manager
          </NavItem>
          <NavItem aria-label="General Ledger" to={`${pathPrefix}/general-ledger`}>
            General Ledger
          </NavItem>
          <NavItem aria-label="Income Statement" to={`${pathPrefix}/income-statement`}>
            Income Statement
          </NavItem>
          <NavItem aria-label="Balance Sheet" to={`${pathPrefix}/balance-sheet`}>
            Balance Sheet
          </NavItem>
          <NavItem
            aria-label="Transactions"
            to={`${pathPrefix}/transactions`}
            isActive={(match, location) => {
              if (match) {
                return true;
              }
              return !!matchPath(location.pathname, {
                path: `${patternPrefix}/search`,
              });
            }}
          >
            Transactions
          </NavItem>
          <NavItem
            aria-label="Unmatched Receipts"
            to={`${pathPrefix}/unmatched-receipts`}
            isActive={(match, location) => {
              if (match) {
                return true;
              }
              return !!matchPath(location.pathname, {
                path: `${patternPrefix}/unmatched-receipts-search`,
              });
            }}
          >
            Unmatched Receipts
          </NavItem>
          <NavItem aria-label="Excel Documents" to={`${pathPrefix}/excel-documents`}>
            Excel Documents
          </NavItem>
          <NavItem aria-label="Download" to={`${pathPrefix}/download`}>
            Download
          </NavItem>
        </NavSection>
        <NavSection>
          <NavHeader>Tools</NavHeader>
          {isInternalPlaidOcrEnabled && (
            <NavItem aria-label="Statement OCR" to={`${pathPrefix}/statement-ocr`}>
              Statement OCR
            </NavItem>
          )}
          {/* TODO: ENG-4774 Unhide page link once Customer-facing OCR work is ready */}
          {/* <NavItem aria-label="Statement OCR Beta" to={`${pathPrefix}/statement-ocr-beta`}>
            Statement OCR - BETA
          </NavItem> */}
          <NavItem aria-label="Client Details" to={`${pathPrefix}/details`}>
            Client Details
          </NavItem>
          <NavItem aria-label="Review Tool" to={`${pathPrefix}/review-tool`}>
            Review Tool
          </NavItem>
          <NavItem aria-label="Balance Checker" to={`${pathPrefix}/balance-checker`}>
            Balance Checker
          </NavItem>
          <NavItem
            aria-label="Document Entry"
            to={`${pathPrefix}/document-entry`}
            /* This is a special "magic" ID the bootstrap app uses to setup the counters. */
            id="document-entry-menu-item"
          >
            Document Entry
            <span className={LEGACY_NOTIFICATION_COUNT_WRAPPER_CLASS} />
          </NavItem>
          <NavItem
            aria-label="Reconciliation"
            to={`${pathPrefix}/reconciliation`}
            /* This is a special "magic" ID the bootstrap app uses to setup the counters. */
            id="reconciliation-menu-item"
          >
            Reconciliation
            <span className={LEGACY_NOTIFICATION_COUNT_WRAPPER_CLASS} />
          </NavItem>
          <NavItem aria-label="Categorizer Rules" to={`${pathPrefix}/categorizer-rules`}>
            Categorizer Rules
          </NavItem>
          <NavItem aria-label="Ledger Accounts" to={`${pathPrefix}/ledger-accounts`}>
            Ledger Accounts
          </NavItem>
          <NavItem aria-label="Import to Excel" to={`${pathPrefix}/import-excel`}>
            Import to Excel
          </NavItem>
        </NavSection>
      </NavLayout>
      <NavBottomCluster>
        <NavBottomClusterAction
          aria-label="run MITO"
          onClick={handleRunMito}
          title="Run MITO"
          style={{
            opacity: isRunningMito ? 0.5 : 1,
            cursor: isRunningMito ? 'not-allowed' : 'pointer',
            pointerEvents: isRunningMito ? 'none' : 'auto',
          }}
        >
          {isRunningMito ? <WrappedSpinner /> : <NavButton>Run MITO</NavButton>}
        </NavBottomClusterAction>
      </NavBottomCluster>
      <NavBottomCluster>
        <NavBottomClusterAction
          aria-label="Settings"
          onClick={() => dialogsManager.init('settings')}
          title="Settings"
        >
          <Icon color={colors.white} icon="man" />
        </NavBottomClusterAction>
        <NavBottomClusterAction
          aria-label="Ledger Quick Add"
          onClick={() => openAddLedger(clientId)}
          title="Create Ledger"
        >
          <Icon color={colors.white} icon="plus" />
        </NavBottomClusterAction>
        <NavBottomClusterLink
          aria-label="Sign Out"
          href={`${env.location.websiteRoot}/logout`}
          title="Sign Out"
        >
          <Icon color={colors.white} icon="logout" />
        </NavBottomClusterLink>
      </NavBottomCluster>
      <GlobalStyle />
    </SidebarLayout>
  );
};

export default BkToolsSidebar;
